// src/pages/sprintcentral/SprintCentralProjects.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralProjects = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    name: "",
    key: "",          // e.g. PROJ-1, CRM-2025
    description: "",
    startDate: "",
    targetEndDate: "",
    status: "Planning", // Planning | Active | Completed | On Hold
  };

  const [formData, setFormData] = useState(initialForm);

  const demo = [
    {
      id: 1,
      name: "Website Redesign 2026",
      key: "WEB-26",
      description: "Complete overhaul of company website + new brand identity",
      startDate: "2026-02-01",
      targetEndDate: "2026-06-30",
      status: "Active",
      progress: 45,
      owner: "Design Team",
      priority: "High",
      createdAt: new Date().toISOString(),
    },
    {
      id: 2,
      name: "ERP Integration Phase 1",
      key: "ERP-01",
      description: "Integrate Tally with custom inventory and billing system",
      startDate: "2026-01-15",
      targetEndDate: "2026-04-30",
      status: "Planning",
      progress: 10,
      owner: "IT Department",
      priority: "High",
      createdAt: new Date().toISOString(),
    },
    {
      id: 3,
      name: "Customer Support Portal",
      key: "CSP-02",
      description: "Build self-service ticketing portal for customers",
      startDate: "2026-03-01",
      targetEndDate: "2026-05-15",
      status: "Active",
      progress: 30,
      owner: "Product Team",
      priority: "Medium",
      createdAt: new Date().toISOString(),
    },
    {
      id: 4,
      name: "Mobile App Launch",
      key: "APP-10",
      description: "Launch Android & iOS mobile app for customers",
      startDate: "2026-04-10",
      targetEndDate: "2026-09-01",
      status: "Planning",
      progress: 0,
      owner: "Mobile Team",
      priority: "High",
      createdAt: new Date().toISOString(),
    },
    {
      id: 5,
      name: "Marketing Automation Setup",
      key: "MKT-05",
      description: "Email marketing + CRM automation setup",
      startDate: "2026-01-10",
      targetEndDate: "2026-03-20",
      status: "Completed",
      progress: 100,
      owner: "Marketing Team",
      priority: "Medium",
      createdAt: new Date().toISOString(),
    }
  ];
  
  // useEffect(() => {
  //   if (saved) {
  //     setProjects(JSON.parse(saved));
  //   } else {
  //     setProjects(demo);
  //   }
  // }, []);

  const loadSample = () => {
    setProjects(demo);
  };

  const handleAddNew = () => {
    setEditingProject(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (proj) => {
    setEditingProject(proj);
    setFormData({
      name: proj.name,
      key: proj.key,
      description: proj.description || "",
      startDate: proj.startDate || "",
      targetEndDate: proj.targetEndDate || "",
      status: proj.status,
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this project? All sprints, tasks and members will be lost.")) return;
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  const handleOpenProject = (proj) => {
    navigate(`/sprintcentral-projecthub/${proj.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.key.trim()) {
      alert("Project name and key are required.");
      return;
    }

    const newProj = {
      id: editingProject?.id || Date.now(),
      name: formData.name.trim(),
      key: formData.key.trim().toUpperCase(),
      description: formData.description.trim(),
      startDate: formData.startDate,
      targetEndDate: formData.targetEndDate,
      status: formData.status,
      createdAt: editingProject?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (editingProject) {
      setProjects((prev) =>
        prev.map((p) => (p.id === editingProject.id ? newProj : p))
      );
    } else {
      setProjects((prev) => [...prev, newProj]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.key.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const statusColor = {
    Planning: "bg-indigo-600",
    Active: "bg-emerald-600",
    Completed: "bg-gray-600",
    "On Hold": "bg-amber-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🏃 SprintCentral
          </div>
          <div className="text-xs text-gray-400">
            Lightweight Project & Sprint Management for MSMEs
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700 rounded-md"
              >
                ⏻
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
               Sample Projects List
            </button>
            <button
              onClick={handleAddNew}
              className="ml-4 flex items-center justify-center gap-2 text-xs lg:text-sm w-24 lg:w-32 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Project
            </button>
          </div>
          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              style={{width: "100%"}}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Project Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((proj) => (
            <div
              key={proj.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-sm text-white group-hover:text-cyan-300 transition-colors">
                    {proj.name}
                  </h3>
                  <div className="text-sm text-gray-400 font-mono">{proj.key}</div>
                </div>

                <span
                  className={`px-1 py-1 rounded-full text-xs font-medium ${statusColor[proj.status]}`}
                >
                  {proj.status}
                </span>
              </div>

              <p className="mt-3 text-sm text-gray-300 line-clamp-2">
                {proj.description || "No description"}
              </p>

              <div className="mt-4 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                {proj.startDate && (
                  <div>
                    Start: <span className="text-gray-300">{proj.startDate}</span>
                  </div>
                )}
                {proj.targetEndDate && (
                  <div>
                    Target: <span className="text-gray-300">{proj.targetEndDate}</span>
                  </div>
                )}
              </div>

              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => handleOpenProject(proj)}
                  className="flex-1  text-xs lg:text-sm w-24 lg:w-32 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(proj)}
                  className=" text-xs lg:text-sm w-24 lg:w-32 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(proj.id)}
                  className=" text-xs lg:text-sm w-24 lg:w-32 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No projects match your search" : "No projects yet. Create your first one!"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className=" text-xs lg:text-sm w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className=" text-xs lg:text-sm w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* ──── Modal ──── */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingProject ? "Edit Project" : "New Project"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white text-lg lg:text-xl w-12 lg:w-16 "
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Project Name *</label>
                  <input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Mobile App v2"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Project Key *</label>
                  <input
                    required
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg font-mono focus:outline-none focus:border-cyan-500"
                    placeholder="APP-26"
                    maxLength={12}
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Short summary of the project goals..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Target End Date</label>
                    <input
                      type="date"
                      value={formData.targetEndDate}
                      onChange={(e) => setFormData({ ...formData, targetEndDate: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="Planning">Planning</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-7">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className=" text-xs lg:text-sm w-24 lg:w-32 px-2 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className=" text-xs lg:text-sm w-24 lg:w-32 px-2 py-22 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingProject ? "Update Project" : "Create Project"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralProjects;